import React, { ReactNode } from 'react';
import { menuPortalProps } from '@visalex/core';

import { TFunction } from 'i18next';

import { QuestionnaireActionFooter } from '../../Common/QuestionnaireActionFooter';
import { QuestionnairePageOptions } from '../../QuestionnairePage';

import { FreeConsultationFinalPage } from './FreeConsultationFinalPage';
import { FreeConsultationRadio } from './FreeConsultationRadio';
import { FreeConsultationSelect } from './FreeConsultationSelect';

export const getFreeConsultationBasePage = (i: number, t: TFunction): QuestionnairePageOptions => ({
  uuid: `free-consultation-${i}`,
  title: t(`free-consultation.pages.${i}.title`),
  fields: [],
});

export const getFreeConsultationDefaultValues = (query: Record<string, unknown>, countries: Array<string>[]) => {
  const initialCountry = countries.find((country) => country[1] === query.country);

  return {
    country: initialCountry ? { label: initialCountry[0], value: initialCountry[1] } : null,
    nationality: initialCountry ? { label: initialCountry[2], value: initialCountry[1] } : null,
    ...query,
  };
};

const nameToField = (name: string) => ({ name, required: true });

export const getFreeConsultationPages = (
  t: TFunction,
  query: Record<string, string>,
  countries: Array<string>[],
  isLoading: boolean
): QuestionnairePageOptions[] => {
  const finalPageFields = [
    'firstName',
    'lastName',
    'email',
    'language',
    'nationality',
    'phoneNumber',
    'reason',
    'comments',
  ];

  const radioFieldNames = ['admission-refusing', 'visa-violation', 'criminal-record'];

  return [
    {
      ...getFreeConsultationBasePage(0, t),
      fields: ['country'].map(nameToField),
      children: <FreeConsultationSelect t={t} query={query} countries={countries} menuPortalProps={menuPortalProps} />,
    },
    ...[1, 2, 3].map((index) => ({
      ...getFreeConsultationBasePage(index, t),
      fields: [radioFieldNames[index - 1]].map(nameToField),
      children: <FreeConsultationRadio t={t} fieldName={radioFieldNames[index - 1]} index={index} />,
    })),
    {
      ...getFreeConsultationBasePage(4, t),
      customFooter: <QuestionnaireActionFooter text={t('visa-finder.request-consultation')} isLoading={isLoading} />,
      fields: [...finalPageFields].map(nameToField),
      children: <FreeConsultationFinalPage countries={countries} menuPortalProps={menuPortalProps} />,
    },
  ];
};

export const getCurrentPage = (query: Record<string, string>, pages: QuestionnairePageOptions[]) => {
  let initPage = 0;

  pages.forEach((page) => {
    page.fields.forEach(
      (field) => query[field.name] && /country|admission|violation|criminal/.test(field.name) && initPage++
    );
  });

  return Math.min(initPage, pages.length);
};

export const getFreeConsultationValidationMessage = (
  i: number,
  t: TFunction,
  validationStyles: Record<string, string>
): ReactNode => (
  <div className={validationStyles['free-consultation__validation-message']} data-testid="validation-message">
    <strong>{t('free-consultation.error-title')}</strong>
    <p>{t(`free-consultation.pages.${i}.error-description1`)}</p>
    <p>
      <>
        {t(`free-consultation.pages.${i}.error-description2`)}
        <span dangerouslySetInnerHTML={{ __html: t('free-consultation.please-contact-us') }} />
      </>
    </p>
  </div>
);
